import './product-detail.scss';
import Swiper from 'swiper/bundle';
/**
 * Class representing the product detail functionality, including gallery sliders
 * and sticky "Add to Cart" button behavior.
 */
class ProductDetail {
    /**
     * Create a ProductDetail instance.
     * @param {HTMLElement} element - The root element of the product detail component.
     */
    constructor(element) {
        /** @private @type {HTMLElement} */
        this.$productDetail = element;

        /** @private @type {NodeListOf<HTMLElement>} */
        this.$productGalleryItemCount = this.$productDetail.querySelectorAll('[data-product-gallery="count"]');

        /** @private @type {HTMLElement} */
        this.$productGallerySlider = this.$productDetail.querySelector('[data-product-gallery="slider"]');

        /** @private @type {HTMLElement} */
        this.$productGalleryThumbs = this.$productDetail.querySelector('[data-product-gallery="thumbs"]');

        /** @private @type {HTMLElement} */
        this.$productAddToCartButton = this.$productDetail.querySelector('[data-product-detail="add-to-cart"]');

        /** @private @type {HTMLElement} */
        this.$footer = document.querySelector('[data-footer]');
    }

    /**
     * Initialize product detail component.
     * Sets up the gallery slider and sticky add-to-cart button observer.
     */
    initialize() {
        if (this.$productGalleryItemCount.length > 1) {
            this.initGallerySlider();
        }

        if (this.$productAddToCartButton && this.$footer) {
            this.initStickyButtonObserver();
        }

        this.initDescriptionToggle();
    }

    /**
     * Initialize the "See more / See less" toggle for the product description.
     * Collapses long descriptions beyond MAX_CHARS and appends a toggle button.
     * Also resets the collapsed state on window resize.
     */
    initDescriptionToggle() {
        const MAX_CHARS = 300;
        const COLLAPSED_HEIGHT = 96;
        const description = this.$productDetail.querySelector('[data-product-description]');

        if (!description) {
            return;
        }

        const plainText = description.textContent.trim();

        if (plainText.length <= MAX_CHARS) {
            return;
        }

        const fullHeight = description.scrollHeight;

        description.classList.add('product-detail__product-description--is-collapsed');

        const toggleBtn = document.createElement('button');
        toggleBtn.classList.add('product-detail__description-toggle');
        toggleBtn.setAttribute('type', 'button');
        toggleBtn.setAttribute('aria-expanded', 'false');

        const labelMore = description.dataset.labelMore || 'See more';
        const labelLess = description.dataset.labelLess || 'See less';
        toggleBtn.textContent = labelMore;

        toggleBtn.addEventListener('click', () => {
            const isCollapsed = description.classList.contains('product-detail__product-description--is-collapsed');

            if (isCollapsed) {
                description.style.maxHeight = `${fullHeight}px`;
                description.classList.remove('product-detail__product-description--is-collapsed');
                toggleBtn.setAttribute('aria-expanded', 'true');
                toggleBtn.textContent = labelLess;
            } else {
                description.style.maxHeight = `${COLLAPSED_HEIGHT}px`;
                description.classList.add('product-detail__product-description--is-collapsed');
                toggleBtn.setAttribute('aria-expanded', 'false');
                toggleBtn.textContent = labelMore;
            }
        });

        description.insertAdjacentElement('afterend', toggleBtn);

        window.addEventListener('resize', () => {
            description.style.maxHeight = '';
            description.classList.add('product-detail__product-description--is-collapsed');
            toggleBtn.setAttribute('aria-expanded', 'false');
            toggleBtn.textContent = labelMore;
        });
    }

    /**
     * Initialize IntersectionObserver for sticky "Add to Cart" button.
     * Adjusts button position relative to the footer visibility.
     * @private
     */
    initStickyButtonObserver() {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.$productAddToCartButton.style.bottom = `-${this.$footer.offsetHeight}px`;
                    } else {
                        this.$productAddToCartButton.style.bottom = '0';
                    }
                });
            },
            {
                root: null,
                threshold: 0
            }
        );

        observer.observe(this.$footer);
    }

    /**
     * Initialize Swiper gallery sliders (main and thumbnails).
     * Also adds keyboard navigation and focus management for accessibility.
     * @private
     */
    initGallerySlider() {
        const thumbnailGallerySlider = new Swiper(this.$productGalleryThumbs, {        
            slidesPerView: 3,
            scrollOnFocus: true,
            spaceBetween: 4,
            a11y: false
        });

        const mainGallerySlider = new Swiper(this.$productGallerySlider, {
            slidesPerView: 1,
            spaceBetween: 4,
            thumbs: {swiper: thumbnailGallerySlider},           
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            a11y: true,
            zoom: false,
            loop: false,
            autoplay: false,       
        });

        // Add keyboard and focus event listeners for thumbnails
        this.$productGalleryItemCount.forEach((thumbnail, index) => {
            const label = thumbnail.dataset.ariaLabel;
            if (label) thumbnail.setAttribute('aria-label', label);
            /**
             * Handle focus event on a thumbnail to change the main slide
             */
            thumbnail.addEventListener('focus', () => {
                mainGallerySlider.slideTo(index);
                this.updateAriaPressed(index);
            });

            /**
             * Handle keyboard navigation for thumbnails
             * @param {KeyboardEvent} e
             */
            thumbnail.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    mainGallerySlider.slideTo(index);
                    this.updateAriaPressed(index);
                }
                if (e.key === 'ArrowRight' && index < this.$productGalleryItemCount.length - 1) {
                    this.$productGalleryItemCount[index + 1].focus();
                    mainGallerySlider.slideTo(index + 1);
                    this.updateAriaPressed(index + 1);
                } else if (e.key === 'ArrowLeft' && index > 0) {
                    this.$productGalleryItemCount[index - 1].focus();
                    mainGallerySlider.slideTo(index - 1);
                    this.updateAriaPressed(index - 1);
                }
            });
        });
    }

    /**
     * Update aria-pressed attribute for all thumbnails to indicate the active one.
     * @param {number} activeIndex - Index of the currently active thumbnail.
     * @private
     */
    updateAriaPressed(activeIndex) {
        this.$productGalleryItemCount.forEach((thumbnail, index) => {
            thumbnail.setAttribute('aria-pressed', index === activeIndex ? 'true' : 'false');
        });
    }
}

export {ProductDetail};

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;
    if ($context) {
        const $productDetail = $context.querySelectorAll('[data-product-detail="root"]');

        for (let i = 0; i < $productDetail.length; i++) {
            const productDetail = new ProductDetail($productDetail[i]);
            productDetail.initialize();
        }
    }
});
